import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import * as authActions from '../state/auth.actions';
import {
  AuthService,
  LoginApiError,
  LoginCredentials,
  LoginResponse,
} from './auth.service';
import { UserService } from './user.service';

/**
 * The auth write owner: each flow dispatches its trigger action first (reducers
 * flag loading / reset state, session-cache glue effects react), performs the
 * HTTP call, then broadcasts the existing Success/Fail actions consumed by the
 * reducers and the orchestration effects that stay in `auth.effects`
 * (`loginSuccess$`, `remoteLoginFailUi$`).
 *
 * `loginSuccess` is also dispatched by `authInitFactory` on bootstrap, which is
 * why it stays a broadcast action rather than being owned here.
 */
@Injectable({ providedIn: 'root' })
export class AuthFlowService {
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);

  login(credentials: LoginCredentials): void {
    this.store.dispatch(authActions.login({ credentials }));

    this.authService.login(credentials).subscribe({
      next: (response: LoginResponse) =>
        this.store.dispatch(
          authActions.loginSuccess({
            response,
            remember: credentials.remember_me || false,
          }),
        ),
      error: (error: LoginApiError) =>
        this.store.dispatch(authActions.loginFailure({ error: error.message })),
    });
  }

  logout(): void {
    this.store.dispatch(authActions.logout());

    // Clear User service caches regardless of the success of the logout HTTP;
    // authService.logout() is best-effort (swallows API errors) and owns the
    // local credential cleanup + /auth/login navigation.
    this.userService.clearCaches();
    this.authService.logout().subscribe();
  }

  remoteLogin(token: string): void {
    this.store.dispatch(authActions.remoteLogin({ token }));

    this.authService.loginToken(token).subscribe({
      next: (response) => {
        if ((response as any)?.error) {
          this.store.dispatch(
            authActions.remoteLoginFail({
              error: (response as any).message || 'Login failed',
            }),
          );
          return;
        }

        this.store.dispatch(
          authActions.remoteLoginSuccess({
            response: response as LoginResponse,
          }),
        );
      },
      error: (err) =>
        this.store.dispatch(
          authActions.remoteLoginFail({
            error: err?.error?.message || err?.error?.error || 'Login failed',
          }),
        ),
    });
  }
}
